import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Pagination from './Pagination';
import Modal from './Modal';

/**
 * Generic paginated-table-with-search shell for SuperAdmin Data Browser sub-pages.
 *
 * Used by all 8 cross-org SuperAdmin → Data sub-pages (Businesses, Vendors,
 * Invoices, Line items, Categories, SKU catalog, Misc charges, Price history)
 * plus the Price Ranges page in Wave 8. Each entity sub-page passes a column
 * config + an endpoint wrapper around `api.get('/admin/data/<entity>')`.
 *
 * Features:
 * - Search input with 300ms debounce
 * - Pagination via shared Pagination component
 * - Loading state with seedling loader (role="status" for a11y)
 * - Empty state with title + description
 * - Error state with retry button
 * - Row click opens a Modal showing the row's full JSON (drill-down)
 * - Mono columns render with `td-mono` class for monetary/ID values
 *
 * @param {Object} props
 * @param {function} props.endpoint - async (params) => { data: {items, total, page, per_page} }
 *   Receives `{page, per_page, search}` and returns a Promise of an axios-shaped response
 *   (or a bare payload). Both `res.data.items` and `res.items` are supported.
 * @param {Array<{key: string, label: string, mono?: boolean, format?: function}>} props.columns
 *   Column config. `mono: true` renders the cell with monospace font (for IDs, prices).
 *   `format(value, row)` lets the caller transform the raw value (e.g. date formatting).
 * @param {number} [props.defaultPerPage=50] - Initial per-page selection
 * @param {string} [props.searchPlaceholder='Search...'] - Search input placeholder
 * @param {string} [props.emptyTitle='No results'] - Empty state heading
 * @param {string} [props.emptyDescription] - Empty state body copy
 * @returns {JSX.Element}
 *
 * @example
 * <AdminDataBrowser
 *   endpoint={(params) => api.get('/admin/data/vendors', { params })}
 *   columns={[
 *     { key: 'id', label: 'ID', mono: true },
 *     { key: 'name', label: 'Name' },
 *     { key: 'created_at', label: 'Created', format: (v) => new Date(v).toLocaleDateString() },
 *   ]}
 *   searchPlaceholder="Search vendors..."
 * />
 */
const SEARCH_DEBOUNCE_MS = 300;

function AdminDataBrowser({
  endpoint,
  columns,
  defaultPerPage = 50,
  searchPlaceholder = 'Search...',
  emptyTitle = 'No results',
  emptyDescription = 'Try a different search or check back later.',
  onExport,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(defaultPerPage);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drillRow, setDrillRow] = useState(null);
  // Sort state. `sortKey` is the column's `sortKey` (or `key` if not provided);
  // `sortDir` is 'asc'|'desc'. Both null = use backend's default ORDER BY.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [exporting, setExporting] = useState(false);
  const debounceTimer = useRef(null);
  const isFirstSearchEffect = useRef(true);

  /**
   * Click handler for sortable column headers. First click → asc; second
   * click on the same column → desc; third click → clear (back to default).
   */
  const handleSortClick = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey(null);
      setSortDir(null);
    }
    setPage(1);
  };

  const handleExport = async () => {
    if (!onExport) return;
    setExporting(true);
    try {
      await onExport({ search: debouncedSearch, sort: sortKey, sort_dir: sortDir });
    } finally {
      setExporting(false);
    }
  };

  // Debounce search input → updates debouncedSearch after 300ms of inactivity.
  // Skip the first run so we don't re-fire on mount (the fetch effect already does).
  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return undefined;
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search]);

  /**
   * Fetch a page of data from the parent-supplied endpoint.
   * Handles both axios-shaped responses (res.data.items) and bare payloads (res.items).
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, per_page: perPage, search: debouncedSearch };
      if (sortKey) {
        params.sort = sortKey;
        params.sort_dir = sortDir;
      }
      const res = await endpoint(params);
      const payload = res && res.data ? res.data : res;
      setItems((payload && payload.items) || []);
      setTotal((payload && payload.total) || 0);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error)
        || (err && err.message)
        || 'Failed to load data';
      setError(msg);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [endpoint, page, perPage, debouncedSearch, sortKey, sortDir]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="admin-data-browser">
      <div className="admin-data-browser__toolbar">
        <input
          type="text"
          className="input"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search"
        />
        {onExport && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleExport}
            disabled={exporting || loading || items.length === 0}
            aria-label="Export to Excel"
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        )}
      </div>

      {loading && (
        <div className="admin-data-browser__loading" role="status" aria-live="polite">
          <img
            src="/logos/loader/cogslog-loader-light-md.svg"
            width="48"
            height="108"
            alt=""
            aria-hidden="true"
          />
          <span>Loading…</span>
        </div>
      )}

      {error && !loading && (
        <div className="admin-data-browser__error" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={fetchData}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="empty-state" role="status">
          <h3 className="empty-state__title">{emptyTitle}</h3>
          <p className="empty-state__description">{emptyDescription}</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  {columns.map((c) => {
                    // A column is sortable when sortable !== false AND a sortKey
                    // is set (or implicit via the column's `key`). Caller can
                    // explicitly disable per column with `sortable: false`.
                    const colSortKey = c.sortKey || (c.sortable === false ? null : c.key);
                    const isSortable = Boolean(colSortKey);
                    const isActive = isSortable && sortKey === colSortKey;
                    const ariaSort = isActive
                      ? (sortDir === 'desc' ? 'descending' : 'ascending')
                      : 'none';
                    return (
                      <th
                        key={c.key}
                        scope="col"
                        aria-sort={isSortable ? ariaSort : undefined}
                      >
                        {isSortable ? (
                          <button
                            type="button"
                            className="adb-sort-btn"
                            onClick={() => handleSortClick(colSortKey)}
                          >
                            {c.label}
                            <span className="adb-sort-icon" aria-hidden="true">
                              {isActive ? (sortDir === 'desc' ? '▼' : '▲') : '⇅'}
                            </span>
                          </button>
                        ) : (
                          c.label
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {items.map((row, i) => (
                  <tr
                    key={row.id != null ? row.id : i}
                    className="adb-row--clickable"
                    onClick={() => setDrillRow(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setDrillRow(row);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Row ${row.id != null ? row.id : i + 1} — view details`}
                  >
                    {columns.map((c) => {
                      const raw = row[c.key];
                      const display = c.format
                        ? c.format(raw, row)
                        : (raw == null || raw === '' ? '—' : raw);
                      return (
                        <td
                          key={c.key}
                          className={c.mono ? 'td-mono' : undefined}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            totalItems={total}
            currentPage={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={(pp) => { setPerPage(pp); setPage(1); }}
            perPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}

      {drillRow && (
        <Modal
          isOpen={true}
          onClose={() => setDrillRow(null)}
          title="Row details"
          size="large"
        >
          <pre className="adb-detail-pre">
            {JSON.stringify(drillRow, null, 2)}
          </pre>
          <div className="adb-detail-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setDrillRow(null)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

AdminDataBrowser.propTypes = {
  /** Async function called as `endpoint({page, per_page, search})`. */
  endpoint: PropTypes.func.isRequired,
  /** Column config — `key`, `label`, optional `mono` flag, optional `format(value, row)`. */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      mono: PropTypes.bool,
      format: PropTypes.func,
      /** Backend sort key (defaults to `key` if column is sortable). */
      sortKey: PropTypes.string,
      /** Set to false to render the header as plain text (not a sort button). */
      sortable: PropTypes.bool,
    })
  ).isRequired,
  /** Initial per-page selection (default 50). */
  defaultPerPage: PropTypes.number,
  /** Search input placeholder text. */
  searchPlaceholder: PropTypes.string,
  /** Heading shown when result set is empty. */
  emptyTitle: PropTypes.string,
  /** Body copy shown when result set is empty. */
  emptyDescription: PropTypes.string,
  /**
   * Optional async export handler called as `onExport({search, sort, sort_dir})`.
   * When provided, an "Export to Excel" button appears on the toolbar.
   */
  onExport: PropTypes.func,
};

export default AdminDataBrowser;
